<script setup lang="ts">
import { nanoid } from 'nanoid'

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  errorMessage: {
    type: String,
    required: false,
  },
  hint: {
    type: String,
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  autocomplete: {
    type: String as PropType<'no' | 'tel' | 'username'>,
    default: 'no',
  },
  defaultCountryCode: {
    type: String as PropType<PhoneNumberCountryCode>,
    required: true,
  },
})

const emit = defineEmits({
  'update:modelValue': (_value: string) => true,
  'validate': () => true,
})

watch(() => props.defaultCountryCode, () => {
  e164Value.value = formatNumber(props.modelValue, 'E.164', countryCode.value)
  normalizedValue.value = formatNumber(e164Value.value, 'INTERNATIONAL', countryCode.value)
  emit('update:modelValue', e164Value.value)
  emit('validate')
})

function handleUpdate(newValue?: string) {
  normalizedValue.value = newValue ?? ''
  e164Value.value = formatNumber(newValue, 'E.164', countryCode.value)
  emit('update:modelValue', e164Value.value)
  emit('validate')
}

function handleBlur() {
  normalizedValue.value = formatNumber(e164Value.value, 'INTERNATIONAL', countryCode.value)
  emit('validate')
}

const normalizedValue = ref('')
const e164Value = ref('')

const countryCode = computed(() => {
  return guessCountryCode(e164Value.value, props.defaultCountryCode)
})

onMounted(() => {
  e164Value.value = formatNumber(props.modelValue, 'E.164', countryCode.value)
  normalizedValue.value = formatNumber(props.modelValue, 'INTERNATIONAL', countryCode.value)
})

watch(() => props.modelValue, () => {
  const newE164Value = formatNumber(props.modelValue, 'E.164', countryCode.value)
  if (e164Value.value !== newE164Value) {
    e164Value.value = newE164Value
    normalizedValue.value = formatNumber(props.modelValue, 'INTERNATIONAL', countryCode.value)
  }
})

function formatNumber(
  value: string | undefined,
  outputNumberFormat: PhoneNumberFormat,
  countryCode: PhoneNumberCountryCode,
) {
  if (!value) {
    return ''
  }
  value = value.trim()
  return parseAndFormatNumber(value, outputNumberFormat, countryCode) || value
}

const id = nanoid()
</script>

<template>
  <div class="w-full">
    <InputGroup>
      <InputGroupAddon>
        <span
          :class="['fi', `fi-${countryCode.toLowerCase()}`]"
          style="font-size: 1.2em; margin-left: 5px; margin-right: 5px;"
        />
      </InputGroupAddon>
      <FloatLabel variant="in">
        <InputText
          v-bind-once="{ id }"
          :model-value="normalizedValue"
          class="h-[50px]"
          :autocomplete="props.autocomplete"
          :disabled="props.disabled"
          :name="props.autocomplete === 'no' ? undefined : props.autocomplete"
          :invalid="!!props.errorMessage"
          @update:model-value="handleUpdate"
          @blur="handleBlur"
        />
        <label v-bind-once="{ for: id }">{{ props.label }}</label>
      </FloatLabel>
    </InputGroup>
    <span
      v-if="props.hint"
      :class="['block', props.errorMessage ? 'text-red-600' : 'text-slate-500', 'text-xs', 'mt-1', 'ml-3', 'mr-3']"
    >{{ props.hint }}</span>
    <span
      v-if="props.errorMessage"
      class="block text-red-600 text-xs mt-1 ml-3 mr-3"
    >{{ props.errorMessage }}</span>
  </div>
</template>
